import { Box, Card, CardContent, Grid, Typography, Fade, Slide, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import LandingSection from "../../shared/components/landing-section";
import theme from "../../theme";
import "../../shared/css/about.css";

export default function AboutUsPage() {
    const { t } = useTranslation();

    return (
        <Fade in timeout={1200}>
            <Box
                sx={{
                    minHeight: '100vh',
                    background: `
                        linear-gradient(135deg, ${theme.palette.primary.main} 0%, #1e3a8a 50%, #3b82f6 100%),
                        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
                    `,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                        zIndex: 1,
                    }
                }}
            >
                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 10 } }}>
                    {/* Hero Section */}
                    <LandingSection
                        id={"description"}
                        style={{
                            width: '100%',
                            marginBottom: 64,
                        }}>
                        <Box sx={{ textAlign: 'center', mb: 8, mt: { xs: 6, md: 10 } }}>
                            <Slide in direction="down" timeout={1000}>
                                <Typography 
                                    variant="h2" 
                                    color='white' 
                                    fontWeight='800' 
                                    letterSpacing={1} 
                                    sx={{ 
                                        mb: 3, 
                                        textShadow: '0 4px 16px rgba(0,0,0,0.3)',
                                        fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                                        background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    {t("aboutUs.whatWeDo")}
                                </Typography>
                            </Slide>
                            <Slide in direction="up" timeout={1200}>
                                <Typography 
                                    variant="h5" 
                                    color='rgba(255,255,255,0.95)' 
                                    sx={{ 
                                        maxWidth: 800, 
                                        mx: 'auto', 
                                        fontSize: { xs: 18, md: 22 }, 
                                        lineHeight: 1.8,
                                        textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        fontWeight: 400,
                                    }}
                                >
                                    {t("aboutUs.description")}
                                </Typography>
                            </Slide>
                        </Box>
                    </LandingSection>

                    {/* How We Do It Section */}
                    <Box sx={{ width: '100%', mb: 10 }}>
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography 
                                variant="h3" 
                                color='white' 
                                fontWeight='700' 
                                letterSpacing={1} 
                                sx={{ 
                                    mb: 3, 
                                    textShadow: '0 3px 12px rgba(0,0,0,0.25)',
                                    fontSize: { xs: '2rem', md: '2.8rem' }
                                }}
                            >
                                {t("aboutUs.howWeDoIt")}
                            </Typography>
                            <Typography 
                                variant="h6" 
                                color='rgba(255,255,255,0.9)' 
                                sx={{ 
                                    maxWidth: 700, 
                                    mx: 'auto', 
                                    fontSize: { xs: 16, md: 20 }, 
                                    lineHeight: 1.7, 
                                    textShadow: '0 1px 4px rgba(0,0,0,0.15)',
                                    fontWeight: 400,
                                }}
                            >
                                {t("aboutUs.howWeDoItDescription")}
                            </Typography>
                        </Box>

                        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mb: 4 }}>
                            {[{
                                img: require("../../assets/searchAndSelection.png"),
                                text: t("aboutUs.howWeDoitList.step1")
                            }, {
                                img: require("../../assets/passport.png"),
                                text: t("aboutUs.howWeDoitList.step2")
                            }, {
                                img: require("../../assets/support.png"),
                                text: t("aboutUs.howWeDoitList.step3")
                            }].map((item, idx) => (
                                <Grid item xs={12} md={4} key={idx}>
                                    <Slide in direction="up" style={{ transitionDelay: `${idx * 300}ms` }}>
                                        <Card sx={{
                                            position: 'relative',
                                            background: 'rgba(255,255,255,0.15)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            borderRadius: 4,
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.1)',
                                            height: 380,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            overflow: 'hidden',
                                            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            '&:hover': { 
                                                transform: 'translateY(-8px) scale(1.02)', 
                                                boxShadow: '0 32px 64px rgba(0,0,0,0.2), 0 16px 32px rgba(0,0,0,0.15)',
                                            },
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundImage: `url(${item.img})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                filter: 'blur(1px) brightness(0.7) contrast(1.1)',
                                                zIndex: 1,
                                                transition: 'filter 0.4s ease',
                                            }} />
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(to top, rgba(30,58,138,0.7) 0%, rgba(59,130,246,0.3) 50%, rgba(255,255,255,0.1) 100%)',
                                                zIndex: 2,
                                            }} />
                                            <CardContent sx={{ 
                                                width: '100%', 
                                                textAlign: 'center', 
                                                p: 4, 
                                                position: 'relative', 
                                                zIndex: 3,
                                                background: 'rgba(0,0,0,0.2)',
                                                backdropFilter: 'blur(10px)',
                                            }}>
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight={600} 
                                                    color="white" 
                                                    sx={{ 
                                                        textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                                                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {item.text}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Slide>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Fourth Card - Full Width */}
                        <Grid container>
                            <Grid item xs={12}>
                                <Slide in direction="up" style={{ transitionDelay: `900ms` }}>
                                    <Card sx={{
                                        position: 'relative',
                                        background: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 4,
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.1)',
                                        height: 380,
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        overflow: 'hidden',
                                        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                        '&:hover': { 
                                            transform: 'translateY(-8px) scale(1.01)', 
                                            boxShadow: '0 32px 64px rgba(0,0,0,0.2), 0 16px 32px rgba(0,0,0,0.15)',
                                        },
                                    }}>
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${require("../../assets/transparencia.png")})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            filter: 'blur(1px) brightness(0.7) contrast(1.1)',
                                            zIndex: 1,
                                        }} />
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(to top, rgba(30,58,138,0.7) 0%, rgba(59,130,246,0.3) 50%, rgba(255,255,255,0.1) 100%)',
                                            zIndex: 2,
                                        }} />
                                        <CardContent sx={{ 
                                            width: '100%', 
                                            textAlign: 'center', 
                                            p: 4, 
                                            position: 'relative', 
                                            zIndex: 3,
                                            background: 'rgba(0,0,0,0.2)',
                                            backdropFilter: 'blur(10px)',
                                        }}>
                                            <Typography 
                                                variant="h5" 
                                                fontWeight={600} 
                                                color="white" 
                                                sx={{ 
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                                                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {t("aboutUs.howWeDoitList.step4")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Slide>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Mission & Vision Section */}
                    <Box sx={{ width: '100%', mt: 12 }}>
                        <Box sx={{ textAlign: 'center', mb: 8 }}>
                            <Typography 
                                variant="h3" 
                                color='white' 
                                fontWeight='700' 
                                letterSpacing={1} 
                                sx={{ 
                                    mb: 2, 
                                    textShadow: '0 3px 12px rgba(0,0,0,0.25)',
                                    fontSize: { xs: '2rem', md: '2.8rem' }
                                }}
                            >
                                {t("aboutUs.ourMision") + ' & ' + t("aboutUs.ourVision")}
                            </Typography>
                        </Box>
                        <Grid container spacing={{ xs: 4, md: 6 }}>
                            <Grid item xs={12} md={6}>
                                <Slide in direction="left" style={{ transitionDelay: `400ms` }}>
                                    <Box sx={{
                                        p: { xs: 4, md: 6 },
                                        borderRadius: 5,
                                        background: 'rgba(255,255,255,0.98)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        boxShadow: '0 24px 48px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.1)',
                                        minHeight: 280,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 32px 64px rgba(0,0,0,0.2), 0 12px 24px rgba(0,0,0,0.15)',
                                        }
                                    }}>
                                        <Typography 
                                            variant="h4" 
                                            color={theme.palette.primary.main} 
                                            fontWeight='700' 
                                            sx={{ 
                                                mb: 3,
                                                fontSize: { xs: '1.5rem', md: '2rem' }
                                            }}
                                        >
                                            {t("aboutUs.ourMision")}
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            color='text.primary' 
                                            sx={{ 
                                                fontSize: { xs: 16, md: 18 }, 
                                                lineHeight: 1.8,
                                                fontWeight: 400,
                                            }}
                                        >
                                            {t("aboutUs.mision")}
                                        </Typography>
                                    </Box>
                                </Slide>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Slide in direction="right" style={{ transitionDelay: `600ms` }}>
                                    <Box sx={{
                                        p: { xs: 4, md: 6 },
                                        borderRadius: 5,
                                        background: 'rgba(255,255,255,0.98)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        boxShadow: '0 24px 48px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.1)',
                                        minHeight: 280,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 32px 64px rgba(0,0,0,0.2), 0 12px 24px rgba(0,0,0,0.15)',
                                        }
                                    }}>
                                        <Typography 
                                            variant="h4" 
                                            color={theme.palette.primary.main} 
                                            fontWeight='700' 
                                            sx={{ 
                                                mb: 3,
                                                fontSize: { xs: '1.5rem', md: '2rem' }
                                            }}
                                        >
                                            {t("aboutUs.ourVision")}
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            color='text.primary' 
                                            sx={{ 
                                                fontSize: { xs: 16, md: 18 }, 
                                                lineHeight: 1.8,
                                                fontWeight: 400,
                                            }}
                                        >
                                            {t("aboutUs.vision")}
                                        </Typography>
                                    </Box>
                                </Slide>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </Box>
        </Fade>
    );
}